import { ALL_SITES } from '@tempus/trials-types';

import paths, {
  getAllPatientsPageUrl,
  getCarePathwaysPageUrl,
  getMatchReviewPageUrl,
  getPathwayPatientsPageUrl,
  getSiteTrialPageUrl,
  getTempusNursePageUrl,
  PATIENT_WORKSPACE_CP_QUEUE_TAB_QUERY,
} from '~/routes/paths';
import {
  decodePatientWorkspaceOrigin,
  getCarePathwayScreeningLocation,
  type PatientWorkspaceOrigin,
} from '~/routes/patientWorkspaceOrigin';
import { PatientTrackerStateId } from '~/store/api/types/patientTrackerStore';

import { resolveWorkspacePatientSiteId } from './patientWorkspaceNavigation';

/** Crumb ids owned by the workspace trail. Tracker lists reuse {@link PatientTrackerStateId}. */
export enum PatientWorkspaceBreadcrumbId {
  TRIALS = 'patient_workspace_trials',
  SITE_TRIAL = 'patient_workspace_site_trial',
  CARE_PATHWAYS_HUB = 'patient_workspace_care_pathways_hub',
  CARE_PATHWAY_QUEUE = 'patient_workspace_care_pathway_queue',
  SCREEN = 'screen',
}

/** Breadcrumb segment before the patient name on the unified `/patient/...` workspace. */
export type PatientWorkspaceParentBreadcrumb = {
  text: string;
  id: string;
  href: string;
  /** Passed through to `navigate({ search })`. */
  search?: string;
};

export type PatientWorkspaceBreadcrumbContext = {
  /** Global site selector. Tracker-list crumbs follow this, including All Sites. */
  siteId: string;
  /** Route `institutionId`. Care-pathway and site-trial crumbs prefer it when the selector is All Sites. */
  institutionId?: string;
  /** Display name for the site-trial crumb; falls back to a generic label while loading. */
  trialName?: string;
};

/** Tempus Nurse, Match Review, and All Patients follow the global site selector, including All Sites. */
function resolveTrackerListBreadcrumbSiteId(siteId: string): string {
  return siteId.trim() || ALL_SITES;
}

/** Care-pathway and site-trial URLs need a Salesforce institution when the global selector is All Sites. */
function resolveLinkedBreadcrumbSiteId(institutionId: string | undefined, siteId: string): string {
  return resolveWorkspacePatientSiteId(institutionId, siteId) ?? siteId ?? '';
}

function allPatientsCrumb(siteId: string): PatientWorkspaceParentBreadcrumb {
  return {
    text: 'All Patients',
    id: PatientTrackerStateId.ALL_PATIENTS_PAGE,
    href: getAllPatientsPageUrl(siteId),
  };
}

function trialsCrumb(): PatientWorkspaceParentBreadcrumb {
  return {
    text: 'Trials',
    id: PatientWorkspaceBreadcrumbId.TRIALS,
    href: paths.TRIAL_MATERIALS,
  };
}

function splitHrefAndSearch(url: string): { href: string; search?: string } {
  const queryIndex = url.indexOf('?');
  if (queryIndex === -1) {
    return { href: url };
  }
  return { href: url.slice(0, queryIndex), search: url.slice(queryIndex) };
}

function patientTrackerCrumbs(
  origin: Extract<PatientWorkspaceOrigin, { kind: 'patientTracker' }>,
  siteId: string,
): PatientWorkspaceParentBreadcrumb[] {
  switch (origin.page) {
    case PatientTrackerStateId.MATCH_REVIEW_PAGE:
      return [
        {
          text: 'Match Review',
          id: PatientTrackerStateId.MATCH_REVIEW_PAGE,
          href: getMatchReviewPageUrl(siteId),
        },
      ];
    case PatientTrackerStateId.TEMPUS_NURSE_PAGE:
      return [
        {
          text: 'Tempus Nurse',
          id: PatientTrackerStateId.TEMPUS_NURSE_PAGE,
          href: getTempusNursePageUrl(siteId),
        },
      ];
    case PatientTrackerStateId.MATERIALS_PAGE:
      if (!origin.trialId) {
        return [trialsCrumb()];
      }
      return [
        {
          text: 'Trial Documents',
          id: PatientTrackerStateId.MATERIALS_PAGE,
          href: paths.TRIAL_DOCUMENTS.replace(':trialId', origin.trialId),
          search: '?documentsPageTab=Patients',
        },
      ];
    default:
      return [allPatientsCrumb(siteId)];
  }
}

/** Breadcrumb segments before the patient name, derived only from the workspace origin. */
export function buildPatientWorkspaceBreadcrumbPrefix(
  origin: PatientWorkspaceOrigin,
  context: PatientWorkspaceBreadcrumbContext,
): PatientWorkspaceParentBreadcrumb[] {
  const trackerListSiteId = resolveTrackerListBreadcrumbSiteId(context.siteId);
  const linkedSiteId = resolveLinkedBreadcrumbSiteId(context.institutionId, context.siteId);
  const carePathwaysHub: PatientWorkspaceParentBreadcrumb = {
    text: 'Care Pathways',
    id: PatientWorkspaceBreadcrumbId.CARE_PATHWAYS_HUB,
    href: getCarePathwaysPageUrl(linkedSiteId),
  };

  switch (origin.kind) {
    case 'patientTracker':
      return patientTrackerCrumbs(origin, trackerListSiteId);
    case 'siteTrial': {
      if (!origin.trialId) {
        return [trialsCrumb()];
      }
      const location = splitHrefAndSearch(getSiteTrialPageUrl(origin.trialId, linkedSiteId, origin.tab));
      return [
        trialsCrumb(),
        {
          text: context.trialName || 'Site trial',
          id: PatientWorkspaceBreadcrumbId.SITE_TRIAL,
          href: location.href,
          ...(location.search ? { search: location.search } : {}),
        },
      ];
    }
    case 'carePathwayHub':
      return [carePathwaysHub];
    case 'carePathwayQueue':
      if (!linkedSiteId) {
        return [carePathwaysHub];
      }
      return [
        carePathwaysHub,
        {
          text: origin.pathwayName || 'Patient list',
          id: PatientWorkspaceBreadcrumbId.CARE_PATHWAY_QUEUE,
          href: getPathwayPatientsPageUrl(linkedSiteId, origin.pathwayId),
          ...(origin.tab
            ? { search: `?${PATIENT_WORKSPACE_CP_QUEUE_TAB_QUERY}=${encodeURIComponent(origin.tab)}` }
            : {}),
        },
      ];
    case 'carePathwayScreening': {
      const location = getCarePathwayScreeningLocation(origin);
      return [
        carePathwaysHub,
        {
          text: 'Screen',
          id: PatientWorkspaceBreadcrumbId.SCREEN,
          href: location.pathname,
          ...(location.search ? { search: location.search } : {}),
        },
      ];
    }
    case 'none':
      return [allPatientsCrumb(trackerListSiteId)];
  }
}

/** Convenience wrapper: decode the workspace origin from `search`, then build the prefix. */
export function resolvePatientWorkspaceBreadcrumbPrefix(
  input: PatientWorkspaceBreadcrumbContext & { search: string },
): PatientWorkspaceParentBreadcrumb[] {
  return buildPatientWorkspaceBreadcrumbPrefix(decodePatientWorkspaceOrigin(input.search), input);
}
